import { Box, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useMemo, useState } from "react";
import {
  useMilkQualityPerFarmerPeriod,
  useMilkQualityTrend,
} from "../hooks/useInsightsDashboardQueries";
import InsightsSection from "../InsightsSection";
import MilkQualityAlertCard from "./MilkQualityAlertCard";
import MilkQualityDistributionChart from "./MilkQualityDistributionChart";
import MilkQualityTrendChart from "./MilkQualityTrendChart";
import MilkVolumeTrendChart from "./MilkVolumeTrendChart";
import StatTile from "./StatTile";

type PeriodKey = "6m" | "12m" | "all";

const PERIOD_OPTIONS: {
  value: PeriodKey;
  label: string;
  months: number | null;
}[] = [
  { value: "6m", label: "Last 6 months", months: 6 },
  { value: "12m", label: "Last 12 months", months: 12 },
  { value: "all", label: "All time", months: null },
];

const SubCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

function getPeriod(row: { "MilkQuality.periodStart": string }): string {
  return String(row["MilkQuality.periodStart"]).split("T")[0];
}

/** Latest period present in the data, used as the anchor for relative ranges. */
function maxPeriod(periods: string[]): string | null {
  let max: string | null = null;
  for (const p of periods) {
    if (max === null || p > max) max = p;
  }
  return max;
}

function cutoffFor(anchor: string, months: number): string {
  const d = new Date(anchor);
  d.setMonth(d.getMonth() - months);
  return d.toISOString().split("T")[0];
}

function fmt(value: number | null, maxDecimals: number): string {
  if (value == null || isNaN(value)) return "-";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/** Compact volume: 3,200,000 -> "3.2M", 46,000 -> "46k". */
function fmtVolume(value: number | null): string {
  if (value == null || isNaN(value)) return "-";
  if (value >= 1_000_000)
    return `${(value / 1_000_000).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    })}M`;
  if (value >= 1000)
    return `${(value / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })}k`;
  return fmt(value, 0);
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, color: "#18181b", mt: 1 }}
    >
      {children}
    </Typography>
  );
}

export default function MilkQualitySection() {
  const [period, setPeriod] = useState<PeriodKey>("12m");

  const { data: perFarmer, isLoading: perFarmerLoading } =
    useMilkQualityPerFarmerPeriod();
  const { data: trend, isLoading: trendLoading } = useMilkQualityTrend();

  const anchor = useMemo(() => {
    const periods = [
      ...(trend?.data ?? []).map(getPeriod),
      ...(perFarmer?.data ?? []).map(getPeriod),
    ];
    return maxPeriod(periods);
  }, [trend?.data, perFarmer?.data]);

  const months = PERIOD_OPTIONS.find((o) => o.value === period)?.months ?? null;
  const cutoff = anchor && months != null ? cutoffFor(anchor, months) : null;

  const trendFiltered = useMemo(() => {
    if (!trend?.data) return undefined;
    if (!cutoff) return trend.data;
    return trend.data.filter((r) => getPeriod(r) >= cutoff);
  }, [trend?.data, cutoff]);

  const perFarmerFiltered = useMemo(() => {
    if (!perFarmer?.data) return undefined;
    if (!cutoff) return perFarmer.data;
    return perFarmer.data.filter((r) => getPeriod(r) >= cutoff);
  }, [perFarmer?.data, cutoff]);

  // Headline figures always reflect the latest month with data, regardless of
  // the selected chart period — they are a current snapshot, not a range total.
  const summary = useMemo(() => {
    const rows = trend?.data ?? [];
    if (rows.length === 0)
      return { fat: null, protein: null, totalVolume: null, period: null };
    let latest = rows[0];
    for (const r of rows) {
      if (getPeriod(r) > getPeriod(latest)) latest = r;
    }
    const num = (v: unknown) => {
      const n = Number(v);
      return isNaN(n) ? null : n;
    };
    const avg = Number(latest["MilkQuality.avgVolumeLiters"]);
    const count = Number(latest["MilkQuality.count"]);
    return {
      fat: num(latest["MilkQuality.avgFatPercent"]),
      protein: num(latest["MilkQuality.avgProteinPercent"]),
      totalVolume: !isNaN(avg) && !isNaN(count) ? avg * count : null,
      period: getPeriod(latest),
    };
  }, [trend?.data]);

  const latestMonthLabel = summary.period
    ? new Date(summary.period).toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <InsightsSection title="Milk Quality">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          How milk quality is holding up while farms adopt regenerative
          practices.
        </Typography>
        <TextField
          select
          size="small"
          value={period}
          onChange={(e) => setPeriod(e.target.value as PeriodKey)}
          slotProps={{ select: { native: true } }}
          sx={{
            minWidth: 160,
            backgroundColor: "white",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COLORS.bellflower[300],
            },
          }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </TextField>
      </Box>

      {/* ── Quality & Volume ─────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SubHeading>Quality &amp; Volume</SubHeading>
        <SubCard>
          <Typography
            variant="caption"
            sx={{ color: "#71717a", display: "block", mb: 1.5 }}
          >
            Latest reporting month
            {latestMonthLabel ? ` · ${latestMonthLabel}` : ""}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <StatTile
              label="Avg. Butterfat"
              value={fmt(summary.fat, 2)}
              unit="%"
              dotColor={COLORS.dahlia[500]}
            />
            <StatTile
              label="Avg. Protein"
              value={fmt(summary.protein, 2)}
              unit="%"
              dotColor={COLORS.bellflower[500]}
            />
            <StatTile
              label="Total Volume"
              value={fmtVolume(summary.totalVolume)}
              unit="litres"
              dotColor={COLORS.daisy[700]}
            />
          </Box>
        </SubCard>
        <MilkQualityTrendChart data={trendFiltered} isLoading={trendLoading} />
        <MilkVolumeTrendChart data={trendFiltered} isLoading={trendLoading} />

        {/* ── Somatic Cell Count ─────────────────────────── */}
        <SubHeading>Somatic Cell Count</SubHeading>
        <MilkQualityAlertCard
          data={perFarmerFiltered}
          isLoading={perFarmerLoading}
          metrics={["scc"]}
        />
        <MilkQualityDistributionChart
          title="Farmer Distribution"
          data={perFarmerFiltered}
          metric="scc"
          isLoading={perFarmerLoading}
        />

        {/* ── Total Bacterial Count & milk safety ────────── */}
        <SubHeading>Total Bacterial Count &amp; Safety</SubHeading>
        <MilkQualityAlertCard
          data={perFarmerFiltered}
          isLoading={perFarmerLoading}
          metrics={["tbc", "antibiotic"]}
        />
        <MilkQualityDistributionChart
          title="Farmer Distribution"
          data={perFarmerFiltered}
          metric="tbc"
          isLoading={perFarmerLoading}
        />
      </Box>
    </InsightsSection>
  );
}
